import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Stack,
} from "@mui/material";

import { useIntl } from "react-intl";
import { useAuth } from "../../providers/AuthProvider";
import DraggablePaper from "../../components/common/DraggablePaper";
import { useNotification } from "../../providers/NotificationProvider";
import {
  createProjectWorkflow,
  createProgramWorkflow,
  updateWorkflow,
} from "../../api/memberApi";

const BUSINESS_STATES = ["Planned", "Executing", "Completed"];

const toInputValue = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export default function WorkflowFormDialog({
  open,
  onClose,
  onSaved,
  workflow,        // null = 新規追加, object = 編集
  projectId,
  programId,
  actionPlans,     // associatedActionPlans from project/program
}) {
  const intl = useIntl();
  const { companyId } = useAuth();
  const notify = useNotification();

  const isEdit = !!workflow;

  const [actionPlanId, setActionPlanId] = useState("");
  const [workflowName, setWorkflowName] = useState("");
  const [workflowDescription, setWorkflowDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [businessState, setBusinessState] = useState("Planned");
  const [submitting, setSubmitting] = useState(false);

  // バリデーション
  const [actionPlanError, setActionPlanError] = useState(false);
  const [nameError, setNameError] = useState(false);

  // 編集時は既存データで初期化
  useEffect(() => {
    if (!open) return;
    if (isEdit && workflow) {
      setActionPlanId(workflow.actionPlanId || "");
      setWorkflowName(workflow.workflowName || "");
      setWorkflowDescription(workflow.workflowDescription || "");
      setStartDate(toInputValue(workflow.startDate));
      setEndDate(toInputValue(workflow.endDate));
      setBusinessState(workflow.businessState || "Planned");
    } else {
      setActionPlanId("");
      setWorkflowName("");
      setWorkflowDescription("");
      setStartDate("");
      setEndDate("");
      setBusinessState("Planned");
    }
    setActionPlanError(false);
    setNameError(false);
  }, [open, isEdit, workflow]);

  const validate = () => {
    let valid = true;
    if (!workflowName.trim()) { setNameError(true); valid = false; }
    const hasPlans = Array.isArray(actionPlans) && actionPlans.length > 0;
    if (hasPlans && !actionPlanId) { setActionPlanError(true); valid = false; }
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const body = {
        companyId,
        actionPlanId,
        workflowName: workflowName.trim(),
        workflowDescription: workflowDescription.trim(),
        startDate: startDate ? new Date(startDate).toISOString() : null,
        endDate: endDate ? new Date(endDate).toISOString() : null,
        businessState,
      };

      if (isEdit) {
        await updateWorkflow({
          ...body,
          workflowId: workflow.workflowId,
          projectId: workflow.projectId || projectId,
          programId: workflow.programId || programId,
        });
        notify.success(intl.formatMessage({ id: "workflow.updateSuccess" }));
      } else if (projectId) {
        await createProjectWorkflow({ ...body, projectId });
        notify.success(intl.formatMessage({ id: "workflow.createSuccess" }));
      } else if (programId) {
        await createProgramWorkflow({ ...body, programId });
        notify.success(intl.formatMessage({ id: "workflow.createSuccess" }));
      }

      onSaved?.();
      onClose();
    } catch (err) {
      notify.apiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const plans = Array.isArray(actionPlans) ? actionPlans : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
      <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>
        {intl.formatMessage({
          id: isEdit ? "workflow.edit.title" : "workflow.add.title",
        })}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* アクションプラン選択 */}
          {plans.length > 0 && (
            <FormControl fullWidth size="small" required error={actionPlanError}>
              <InputLabel>
                {intl.formatMessage({ id: "workflow.actionPlan" })} *
              </InputLabel>
              <Select
                value={actionPlanId}
                label={`${intl.formatMessage({ id: "workflow.actionPlan" })} *`}
                onChange={(e) => {
                  setActionPlanId(e.target.value);
                  setActionPlanError(false);
                }}
              >
                <MenuItem value="" disabled>
                  {intl.formatMessage({ id: "workflow.actionPlan.placeholder" })}
                </MenuItem>
                {plans.map((ap) => (
                  <MenuItem key={ap.actionPlanId} value={ap.actionPlanId}>
                    {ap.actionPlanName}
                  </MenuItem>
                ))}
              </Select>
              {actionPlanError && (
                <FormHelperText>
                  {intl.formatMessage({ id: "workflow.actionPlan.required" })}
                </FormHelperText>
              )}
            </FormControl>
          )}

          {/* ワークフロー名 */}
          <TextField
            label={intl.formatMessage({ id: "workflow.name" })}
            value={workflowName}
            onChange={(e) => {
              setWorkflowName(e.target.value);
              if (e.target.value.trim()) setNameError(false);
            }}
            size="small"
            fullWidth
            required
            autoFocus
            error={nameError}
            helperText={
              nameError
                ? intl.formatMessage({ id: "workflow.name.required" })
                : ""
            }
          />

          {/* 説明 */}
          <TextField
            label={intl.formatMessage({ id: "workflow.description" })}
            value={workflowDescription}
            onChange={(e) => setWorkflowDescription(e.target.value)}
            multiline
            rows={2}
            size="small"
            fullWidth
          />

          {/* 開始日時 */}
          <TextField
            label={intl.formatMessage({ id: "workflow.startDate" })}
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />

          {/* 終了日時 */}
          <TextField
            label={intl.formatMessage({ id: "workflow.endDate" })}
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />

          {/* 状態 */}
          <FormControl fullWidth size="small">
            <InputLabel>
              {intl.formatMessage({ id: "workflow.status" })}
            </InputLabel>
            <Select
              value={businessState}
              label={intl.formatMessage({ id: "workflow.status" })}
              onChange={(e) => setBusinessState(e.target.value)}
            >
              {BUSINESS_STATES.map((s) => (
                <MenuItem key={s} value={s}>
                  {intl.formatMessage({
                    id: `workflow.status.${s.toLowerCase()}`,
                  })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {intl.formatMessage({ id: "common.cancel" })}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !workflowName.trim()}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            intl.formatMessage({
              id: isEdit ? "common.save" : "common.create",
            })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
